import { useForm } from "../../../hooks/useForm";
import {
  MediaInput,
  useCreateVideoContentAsAdminMutation,
  useUpdateVideoContentAsAdminMutation,
} from "../../../lib/apollo/graphql/generated";
import ActiveStatusSelectBox from "../../common/components/ActiveStatusSelectBox";
import BasicButton from "../../common/components/BasicButton";
import Input from "../../common/components/Input";
import LabeledInput from "../../common/components/LabeledInput";
import Modal, { ModalProps } from "../../common/components/Modal";
import { Video } from "../container/VideosContainer";
import VideoCategorySelectBox from "./VideoCategorySelectBox";

interface UpdateVideoModalProps extends ModalProps {
  video: Video;
}

type VideoForm = {
  title: string;
  description: string;
  youtubeUrl: string;
  minutes: number;
  seconds: number;
  mediaInput: MediaInput;
  statusId: string;
  videoContentCategoryId: string;
};

export default function UpdateVideoModal({
  video,
  open,
  onClose,
}: UpdateVideoModalProps) {
  const videoContentId = video?.videoContentId || "";
  const isCreate = !video.isInserted && !videoContentId;

  const [form, onChange] = useForm<VideoForm>({
    title: video.title,
    description: video.description,
    youtubeUrl: video.youtubeUrl,
    minutes: +video.playTime.split(":")[0],
    seconds: +video.playTime.split(":")[1],
    mediaInput: {
      file: null,
    },
    statusId: video.statusId,
    videoContentCategoryId: video.videoContentCategoryId,
  });

  const [createVideo, { loading: isCreating }] =
    useCreateVideoContentAsAdminMutation({
      onCompleted: () => {
        alert("동영상을 앱에 게시했습니다.");
        onClose();
      },
      refetchQueries: ["GetYoutubeVideosAsAdmin", "GetVideoContentsAsAdmin"],
    });

  const [updateVideo, { loading: isUpdating }] =
    useUpdateVideoContentAsAdminMutation({
      onCompleted: () => {
        alert("동영상 정보를 수정했습니다.");
        onClose();
      },
      refetchQueries: ["GetYoutubeVideosAsAdmin", "GetVideoContentsAsAdmin"],
    });

  const isSubmitDisabled =
    isCreating ||
    isUpdating ||
    Object.values(form).some((value) => !value) ||
    (isCreate && !form.mediaInput.file);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    const playTime = `${form.minutes}:${form.seconds}`;

    if (isCreate) {
      createVideo({
        variables: {
          ...form,
          playTime,
        },
      });
    } else if (videoContentId) {
      updateVideo({
        variables: {
          ...form,
          mediaInput: form.mediaInput.file ? form.mediaInput : null,
          playTime,
          videoContentId,
        },
      });
    }
  };

  return (
    <Modal
      title={`동영상 ${isCreate ? "등록" : "수정"}`}
      open={open}
      onClose={onClose}
      height="900px"
      width="700px"
    >
      <form
        onSubmit={onSubmit}
        className="mt-5 flex h-full flex-col gap-5 overflow-y-auto"
      >
        <LabeledInput
          label="제목"
          value={form.title}
          onValueChange={(value) => onChange("title", value)}
        />
        <div className="flex flex-col gap-1">
          <p className="text-lg font-bold">설명</p>
          <textarea
            value={form.description}
            onChange={(event) => onChange("description", event.target.value)}
            className="h-[200px] resize-none rounded-md border border-zinc-300 p-3"
          />
        </div>
        <div className="flex w-full gap-1">
          <div className="w-full">
            <VideoCategorySelectBox
              value={form.videoContentCategoryId}
              onSelect={(categoryId) =>
                onChange("videoContentCategoryId", categoryId)
              }
            />
          </div>
          <div className="w-full">
            <ActiveStatusSelectBox
              value={form.statusId}
              onSelect={(newStatusId) => onChange("statusId", newStatusId)}
            />
          </div>
        </div>
        <LabeledInput
          label="URL"
          disabled={true}
          value={form.youtubeUrl}
          onValueChange={(value) => onChange("youtubeUrl", value.trim())}
        />
        <div className="flex gap-1">
          <LabeledInput
            label="썸네일"
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];

              if (!file) {
                return;
              }

              onChange("mediaInput", { file });
            }}
          />
          <div className="flex w-1/3 flex-shrink-0 flex-col gap-1">
            <p className="text-lg font-bold">재생시간</p>
            <div className="flex items-center gap-1">
              <Input
                disabled
                type="number"
                placeholder="분"
                value={form.minutes.toString()}
                onValueChange={(value) => onChange("minutes", +value)}
              />
              <span>:</span>
              <Input
                disabled
                type="number"
                placeholder="초"
                max={60}
                min={0}
                value={form.seconds.toString()}
                onValueChange={(value) => {
                  if (+value <= 60 && +value >= 0) {
                    onChange("seconds", +value);
                  } else {
                    alert("0초 이상 60초 이하로 입력해 주세요.");
                  }
                }}
              />
            </div>
          </div>
        </div>
        <BasicButton
          text={isCreate ? "등록" : "수정"}
          type="submit"
          disabled={isSubmitDisabled}
          loading={isCreating || isUpdating}
          className="mt-auto w-1/2 self-center"
        />
      </form>
    </Modal>
  );
}
